import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, X } from 'lucide-react';
import {
  conversionTypeCaption,
  conversionTypeOptions,
  fetchConversionClickOptions,
  fetchConversionRouteOptions,
  type ConversionClickOption,
  type ConversionCriterion,
  type ConversionCriterionType,
  type ConversionRouteOption,
  type ConversionTypeOption,
  type ConversionTypeTotal,
  type WebsiteVisitsConversionConfig,
  type WebsiteVisitsPeriod,
} from '../../lib/websiteVisitsGraph';

function criterionLabel(type: ConversionCriterionType): string {
  if (type === 'route') return 'Page Route';
  if (type === 'click') return 'Element Click';
  if (type === 'event') return 'Event';
  return 'URL Contains';
}

function criterionDotClass(type: ConversionCriterionType): string {
  if (type === 'route') return 'bg-blue-500';
  if (type === 'click') return 'bg-purple-500';
  if (type === 'event') return 'bg-emerald-600';
  return 'bg-orange-500';
}

export function WebsiteConversionTypeTotals({ types }: { types?: ConversionTypeTotal[] }) {
  if (!types?.length) return null;
  return (
    <>
      {types.map((type) => (
        <div key={type.id} className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-emerald-600" />
          <span className="text-lg font-bold tabular-nums text-gray-900 sm:text-xl">
            {type.count.toLocaleString()}
          </span>
          <span className="mb-0.5 self-end text-[10px] text-gray-500">
            {conversionTypeCaption(type.label, type.count)}
          </span>
        </div>
      ))}
    </>
  );
}

interface WebsiteVisitsConversionSettingsPanelProps {
  open: boolean;
  businessId: string | undefined;
  period: WebsiteVisitsPeriod;
  initialConfig: WebsiteVisitsConversionConfig;
  onClose: (next: WebsiteVisitsConversionConfig) => void;
}

/**
 * Same conversion-rate settings drawer as KPI Website Visits Graph:
 * enable toggle + route/click conversion events.
 */
export default function WebsiteVisitsConversionSettingsPanel({
  open,
  businessId,
  period,
  initialConfig,
  onClose,
}: WebsiteVisitsConversionSettingsPanelProps) {
  const [enabled, setEnabled] = useState(false);
  const [criteria, setCriteria] = useState<ConversionCriterion[]>([]);
  const [customTypes, setCustomTypes] = useState<ConversionTypeOption[]>([]);
  const [addingTypeFor, setAddingTypeFor] = useState<number | null>(null);
  const [newTypeLabel, setNewTypeLabel] = useState('');
  const [routes, setRoutes] = useState<ConversionRouteOption[]>([]);
  const [clicks, setClicks] = useState<ConversionClickOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEnabled(initialConfig.enabled);
    setCriteria(initialConfig.criteria.length ? [...initialConfig.criteria] : []);
    setCustomTypes(initialConfig.customTypes?.length ? [...initialConfig.customTypes] : []);
    setAddingTypeFor(null);
    setNewTypeLabel('');
  }, [open, initialConfig]);

  useEffect(() => {
    if (!open || !businessId) return;
    let cancelled = false;
    void (async () => {
      setOptionsLoading(true);
      try {
        const [routeOpts, clickOpts] = await Promise.all([
          fetchConversionRouteOptions(businessId, period),
          fetchConversionClickOptions(businessId, period),
        ]);
        if (!cancelled) {
          setRoutes(routeOpts);
          setClicks(clickOpts);
        }
      } catch (err) {
        console.error('[WebsiteVisitsConversionSettingsPanel] load options', err);
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, businessId, period]);

  const typeChoices = conversionTypeOptions(customTypes);

  const handleClose = () => {
    onClose({
      enabled,
      criteria: criteria
        .filter((c) => c.value)
        .map((c) => ({
          type: c.type,
          value: c.value,
          ...(c.conversionTypeId ? { conversionTypeId: c.conversionTypeId } : {}),
        })),
      customTypes,
    });
  };

  const addCustomType = (index: number) => {
    const label = newTypeLabel.trim();
    if (!label) return;
    const existing = typeChoices.find((type) => type.label.toLowerCase() === label.toLowerCase());
    const id = existing?.id ?? `custom_${crypto.randomUUID()}`;
    if (!existing) setCustomTypes((prev) => [...prev, { id, label }]);
    setCriteria((prev) =>
      prev.map((c, i) => (i === index ? { ...c, conversionTypeId: id } : c)),
    );
    setAddingTypeFor(null);
    setNewTypeLabel('');
  };

  const refreshOptionsForType = async (type: ConversionCriterionType) => {
    if (!businessId || optionsLoading || type === 'url_contains' || type === 'event') return;
    setOptionsLoading(true);
    try {
      if (type === 'route') {
        setRoutes(await fetchConversionRouteOptions(businessId, period));
      } else {
        setClicks(await fetchConversionClickOptions(businessId, period));
      }
    } catch (err) {
      console.error('[WebsiteVisitsConversionSettingsPanel] refresh options', err);
    } finally {
      setOptionsLoading(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999]"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
      }}
    >
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />
      <div
        className="absolute top-0 right-0 h-full w-full max-w-[380px] bg-white shadow-2xl flex flex-col"
        style={{
          animation: 'slideInRight 0.3s ease-out',
          borderTopLeftRadius: '20px',
          borderBottomLeftRadius: '20px',
        }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200/60">
          <h3 className="text-[17px] font-semibold text-gray-900">Settings</h3>
          <button
            type="button"
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 active:bg-gray-200 transition-colors"
          >
            <X className="h-4 w-4 text-gray-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="bg-gray-50 rounded-[14px] p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[15px] font-medium text-gray-900">Conversion Rate</div>
                <div className="text-[12px] text-gray-500 mt-0.5">
                  Count a page, a click, or an event each time it happens
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEnabled((v) => !v)}
                className={`relative w-[51px] h-[31px] rounded-full transition-colors duration-200 ${
                  enabled ? 'bg-green-500' : 'bg-gray-300'
                }`}
                aria-pressed={enabled}
              >
                <div
                  className={`absolute top-[2px] w-[27px] h-[27px] bg-white rounded-full shadow-md transition-transform duration-200 ${
                    enabled ? 'translate-x-[22px]' : 'translate-x-[2px]'
                  }`}
                />
              </button>
            </div>
          </div>

          {enabled ? (
            <div className="mt-4 space-y-3">
              <div className="text-[13px] font-medium text-gray-500 uppercase tracking-wide">
                Conversion Events
              </div>

              {criteria.map((criterion, index) => (
                <div key={index} className="bg-gray-50 rounded-[14px] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${criterionDotClass(criterion.type)}`} />
                      <span className="text-[13px] font-medium text-gray-700">
                        {criterionLabel(criterion.type)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setCriteria((prev) => prev.filter((_, i) => i !== index))}
                      className="w-6 h-6 flex items-center justify-center rounded-full bg-red-50 text-red-500 active:bg-red-100 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={criterion.type}
                      onChange={(e) => {
                        const type = e.target.value as ConversionCriterionType;
                        setCriteria((prev) =>
                          prev.map((c, i) =>
                            i === index
                              ? { ...c, type, value: type === 'event' ? 'form_submit' : '' }
                              : c,
                          ),
                        );
                      }}
                      className="text-[13px] border border-gray-200 rounded-[10px] px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-[120px] shrink-0"
                    >
                      <option value="route">Route</option>
                      <option value="click">Click</option>
                      <option value="url_contains">URL contains</option>
                      <option value="event">Event</option>
                    </select>
                    {criterion.type === 'url_contains' || criterion.type === 'event' ? (
                      <input
                        type="text"
                        value={criterion.value}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCriteria((prev) =>
                            prev.map((c, i) => (i === index ? { ...c, value } : c)),
                          );
                        }}
                        placeholder={criterion.type === 'event' ? 'form_submit' : '/thank-you'}
                        className="text-[13px] border border-gray-200 rounded-[10px] px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex-1 min-w-0"
                      />
                    ) : (
                      <select
                        value={criterion.value}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCriteria((prev) =>
                            prev.map((c, i) => (i === index ? { ...c, value } : c)),
                          );
                        }}
                        onClick={() => void refreshOptionsForType(criterion.type)}
                        className="text-[13px] border border-gray-200 rounded-[10px] px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex-1 min-w-0"
                      >
                        <option value="">-- Select --</option>
                        {criterion.type === 'route'
                          ? routes.map((r) => (
                              <option key={r.route} value={r.route}>
                                {r.route} ({r.sessions} sessions)
                              </option>
                            ))
                          : clicks.map((c) => (
                              <option key={c.identifier} value={c.identifier}>
                                {c.label || c.identifier} ({c.sessions} sessions)
                              </option>
                            ))}
                      </select>
                    )}
                  </div>
                  {criterion.type === 'url_contains' ? (
                    <p className="text-[11px] text-gray-500 mt-2">
                      Counts a page view when the path or URL contains this text.
                    </p>
                  ) : null}
                  {criterion.type === 'event' ? (
                    <p className="text-[11px] text-gray-500 mt-2">
                      Counts each event with this name. form_submit is every form submission.
                    </p>
                  ) : null}
                  {criterion.type === 'route' ? (
                    <p className="text-[11px] text-gray-500 mt-2">
                      Counts each view of this exact path, including a route change that does not reload the page.
                    </p>
                  ) : null}
                  <div className="mt-2">
                    <p className="mb-1 text-[11px] font-medium text-gray-500">Conversion type</p>
                    <select
                      value={criterion.conversionTypeId || ''}
                      onChange={(e) => {
                        const conversionTypeId = e.target.value;
                        setCriteria((prev) =>
                          prev.map((c, i) =>
                            i === index ? { ...c, conversionTypeId: conversionTypeId || undefined } : c,
                          ),
                        );
                      }}
                      className="w-full text-[13px] border border-gray-200 rounded-[10px] px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select type</option>
                      {typeChoices.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    {addingTypeFor === index ? (
                      <div className="mt-2 flex items-center gap-2">
                        <input
                          type="text"
                          value={newTypeLabel}
                          onChange={(e) => setNewTypeLabel(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addCustomType(index);
                            }
                          }}
                          placeholder="New type"
                          className="text-[13px] border border-gray-200 rounded-[10px] px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex-1 min-w-0"
                        />
                        <button
                          type="button"
                          onClick={() => addCustomType(index)}
                          className="shrink-0 rounded-[10px] bg-blue-600 px-2.5 py-1.5 text-[12px] font-semibold text-white"
                        >
                          Add
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setAddingTypeFor(index);
                          setNewTypeLabel('');
                        }}
                        className="mt-1.5 text-[12px] font-medium text-blue-600"
                      >
                        Add a type
                      </button>
                    )}
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={() => setCriteria((prev) => [...prev, { type: 'route', value: '' }])}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-[14px] border-2 border-dashed border-gray-300 text-gray-500 text-[13px] font-medium active:bg-gray-50 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add Conversion Event
              </button>
            </div>
          ) : null}
        </div>
      </div>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>,
    document.body,
  );
}
